import type React from 'react'
import { useCallback } from 'react'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import {
  toCanvasPoint,
  toClientPoint,
} from '@/components/app/editor/tools/shared/helpers/svg-point'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import type { CropDragState } from './use-crop-drag-state'

interface UseCropDragStartOptions {
  state: CropDragState
  bleedWidth: number
  bleedHeight: number
  geometryInteractionDisabled: boolean
  /** The shape's own rotation (`ShapeData.rotation`), the shape-rotate drag's starting angle. */
  shapeRotation: number | undefined
}

/** Pointer-down handlers that start each crop gesture: frame resize, photo pan, photo rotate and
 *  shape rotate. */
export function useCropDragStart({
  state,
  bleedWidth,
  bleedHeight,
  geometryInteractionDisabled,
  shapeRotation,
}: UseCropDragStartOptions) {
  const { svgRef, dragRef, forceUpdate, geomRef, photoFillRef } = state

  /** Shared preamble of every gesture: ignores the pointer unless interaction is enabled, it's
   *  the primary button and (when `requirePhotoFill`) there's a photo to act on; otherwise claims
   *  and captures it. */
  function claimPointer(e: React.PointerEvent, requirePhotoFill: boolean): boolean {
    if (geometryInteractionDisabled) return false
    if (e.button !== 0) return false
    if (requirePhotoFill && !photoFillRef.current) return false
    e.preventDefault()
    e.stopPropagation()
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)

    return true
  }

  /** Rotate pivot: the shape's GEOMETRIC center in client coordinates, plus the pointer's angle
   *  around it at drag-start. */
  function rotationPivot(e: React.PointerEvent) {
    const geom = geomRef.current
    const pivot = toClientPoint(
      svgRef.current,
      geom.x + geom.width / 2,
      geom.y + geom.height / 2,
      bleedWidth,
      bleedHeight
    )
    const startAngleRad = Math.atan2(e.clientY - pivot.y, e.clientX - pivot.x)

    return { centerClientX: pivot.x, centerClientY: pivot.y, startAngleRad }
  }

  const handleResizeDown = useCallback(
    (e: React.PointerEvent, handle: UnifiedSelectionResizeHandle) => {
      if (!claimPointer(e, true)) return

      const { x, y } = toCanvasPoint(svgRef.current, e.clientX, e.clientY, bleedWidth, bleedHeight)

      dragRef.current = {
        type: 'resize',
        handle,
        startCanvasX: x,
        startCanvasY: y,
        initialGeom: geomRef.current,
        initialPhotoFill: photoFillRef.current as ShapePhotoFill,
        aspectLock: {},
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geometryInteractionDisabled, bleedWidth, bleedHeight]
  )

  const handlePanDown = useCallback(
    (e: React.PointerEvent) => {
      if (!claimPointer(e, true)) return

      const { x, y } = toCanvasPoint(svgRef.current, e.clientX, e.clientY, bleedWidth, bleedHeight)
      const photoFill = photoFillRef.current as ShapePhotoFill

      dragRef.current = {
        type: 'pan',
        startCanvasX: x,
        startCanvasY: y,
        initialOffsetX: photoFill.offsetX,
        initialOffsetY: photoFill.offsetY,
      }
      // Re-render immediately (not just on the first pointermove) so the center move badge
      // hides the instant the pan drag starts, not one frame late.
      forceUpdate((n) => n + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geometryInteractionDisabled, bleedWidth, bleedHeight]
  )

  const handleRotateDown = useCallback(
    (e: React.PointerEvent) => {
      if (!claimPointer(e, true)) return

      dragRef.current = {
        type: 'rotate',
        ...rotationPivot(e),
        initialPhotoFill: photoFillRef.current as ShapePhotoFill,
      }
      // Re-render immediately so the move/rotate badges hide the instant the drag starts.
      forceUpdate((n) => n + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geometryInteractionDisabled, bleedWidth, bleedHeight]
  )

  /** Rotates the SHAPE itself (`ShapeData.rotation`) — the "outer select area" rotate handle
   * that normal (non-crop) selection chrome shows above every shape, previously hidden entirely
   * in crop mode once its own chrome got suppressed. Independent of `handleRotateDown` above,
   * which only spins the photo content. Same pivot convention (geometric center, client coords)
   * as both `handleRotateDown` here and normal selection's own rotate handle. */
  const handleShapeRotateDown = useCallback(
    (e: React.PointerEvent) => {
      if (!claimPointer(e, false)) return

      dragRef.current = {
        type: 'shapeRotate',
        ...rotationPivot(e),
        initialRotation: shapeRotation ?? 0,
      }
      forceUpdate((n) => n + 1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geometryInteractionDisabled, bleedWidth, bleedHeight, shapeRotation]
  )

  return { handleResizeDown, handlePanDown, handleRotateDown, handleShapeRotateDown }
}
